import * as React from 'react';

import { useParams } from 'js/lib/useRouter';
import { get as getUser } from 'js/lib/user';
import { LONG_DATE_ONLY_DISPLAY, formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import S12nAccomplishmentPageContent from 'bundles/accomplishments/components/s12n-certificates/S12nAccomplishmentPageContent';
import { useS12nCertificateData } from 'bundles/accomplishments/components/s12n-certificates/useS12nCertificateData';
import CertificateImageDataProvider from 'bundles/accomplishments/components/shared/CertificateImageDataProvider';
import { ENABLE_DEV_OWNER_VIEW } from 'bundles/accomplishments/constants';
import { formatPartnerNames } from 'bundles/accomplishments/utils/formatting';
import { getCertificateSharingLink, getSpecializationLink } from 'bundles/accomplishments/utils/routingUtils';
import { NotFound } from 'bundles/common/components/withNotFound';
import LoadingIcon from 'bundles/courseraLoadingIcon/LoadingIcon';
import CourseraMetatags from 'bundles/seo/components/CourseraMetatags';

import _t from 'i18n!nls/accomplishments';

import 'css!./__styles__/S12nAccomplishmentPage';

const S12nAccomplishmentPage = () => {
  const user = getUser();
  const { code } = useParams();
  const { data: certificateData, loading, error } = useS12nCertificateData(code);

  if (loading) {
    return (
      <div className="rc-SpecializationAccomplishmentPage" data-testid="verify-records-cert-page">
        <LoadingIcon />
      </div>
    );
  }

  if (error || !certificateData) {
    return <NotFound />;
  }

  const isCertificateOwner = user.id === certificateData.userId || ENABLE_DEV_OWNER_VIEW;
  const specializationLink = getSpecializationLink(certificateData.specializationSlug);
  const certificateLink = getCertificateSharingLink(true, code);

  return (
    <CertificateImageDataProvider code={code}>
      {({ certificateImageUrl, metaImageUrl }) => (
        <div className="rc-SpecializationAccomplishmentPage" data-testid="verify-records-cert-page">
          <S12nAccomplishmentPageContent
            userName={certificateData.userName}
            s12nName={certificateData.specializationName}
            courseNames={certificateData.courseCertificates.map((c) => c.courseName)}
            completionDate={formatDateTimeDisplay(certificateData.certificateCompletionDate, LONG_DATE_ONLY_DISPLAY)}
            certificateLink={certificateLink}
            certificateImageUrl={certificateImageUrl}
            userPhoto={(isCertificateOwner && user.photo) || undefined}
            s12nFAQs={certificateData.specializationFAQs}
            partnerNames={formatPartnerNames(certificateData.partnerNames)}
            s12nHomeLink={specializationLink}
            isCertificateOwner={isCertificateOwner}
            slug={certificateData.specializationSlug}
            certificateDescription={certificateData.certificateDescription}
            courseCerts={certificateData.courseCertificates}
          />
          <CourseraMetatags
            metaNameAndProperties={{
              absoluteImageHref: metaImageUrl,
              description: _t(
                'This certificate verifies my successful completion of {partnerName}\'s "{specializationName}" on Coursera',
                {
                  partnerName: certificateData.partnerNames[0],
                  specializationName: certificateData.specializationName,
                }
              ),
              ogAndTwitterTitle: _t('Completion Certificate for {specializationName}', {
                specializationName: certificateData.specializationName,
              }),
              disableCourseraDescriptionSuffix: true,
              disableDescLimit: true,
            }}
          />
        </div>
      )}
    </CertificateImageDataProvider>
  );
};

export default S12nAccomplishmentPage;
